/**
 * The Zoho CRM → WHNCMS field dictionary.
 *
 * Data, not logic. Every entry says "these Zoho names mean this WHNCMS field",
 * and the importer walks the table rather than branching on module names.
 *
 * Three things follow from that, and they are the whole reason for the shape:
 *
 *  - **Aliases, not one name.** Zoho's API returns `Last_Name`; a per-module
 *    CSV export from the web UI is headed `Last Name`. Both are the same
 *    field, so both are listed and the header matcher normalises before
 *    comparing.
 *  - **A wrong name is a one-line data edit.** Zoho's field API names are
 *    documented but vary by edition and can be renamed by an admin. Correcting
 *    one here changes no logic and re-tests nothing.
 *  - **Custom fields are the same problem.** No hard-coded list can know about
 *    `CF_Renewal_Owner`. Anything unmatched is offered in the wizard and lands
 *    in `customFields`, using the same machinery.
 *
 * See `docs/zoho-migration.md` for the ID strategy this sits alongside.
 */

/** How an incoming string is turned into a column value. */
export type ZohoFieldKind =
  | 'string'
  | 'text'
  | 'email'
  | 'phone'
  | 'url'
  | 'number'
  | 'money'
  | 'percent'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'enum'
  /** A reference to another module, carried as that record's Zoho Record Id. */
  | 'lookup';

export interface ZohoFieldMapping {
  /**
   * Accepted Zoho names, most canonical first. Matched case-insensitively
   * with spaces and underscores treated alike, so `Last_Name`, `Last Name`
   * and `last name` all hit.
   */
  zoho: readonly string[];
  /** The WHNCMS property this feeds. */
  field: string;
  kind: ZohoFieldKind;
  /** For `lookup`: the module whose `zohoId` the value refers to. */
  lookupModule?: ZohoModuleName;
  /** For `enum`: Zoho value → WHNCMS enum member. Unlisted values pass through. */
  enumValues?: Readonly<Record<string, string>>;
  /** A row without this cannot be imported; the wizard blocks on it. */
  required?: boolean;
  /**
   * WHNCMS computes this rather than taking the export's value. Listed so the
   * wizard can say "recomputed, not imported" instead of offering the column
   * as an unmapped field the user has to make a decision about.
   *
   * `field` on a derived entry is a label, not necessarily a column: a lead's
   * `score` is recomputed into a real column, while a deal's expected revenue
   * is derived on read and stored nowhere.
   */
  derived?: boolean;
  notes?: string;
}

export type ZohoModuleName =
  | 'Users'
  | 'Accounts'
  | 'Contacts'
  | 'Leads'
  | 'Deals'
  | 'Products'
  | 'Vendors'
  | 'Campaigns'
  | 'Tasks'
  | 'Calls'
  | 'Events'
  | 'Quotes'
  | 'Sales_Orders'
  | 'Invoices'
  | 'Cases'
  | 'Solutions'
  | 'Notes'
  | 'Attachments';

export interface ZohoModuleMapping {
  module: ZohoModuleName;
  /** Display name, and the usual filename stem in an export bundle. */
  label: string;
  /** The WHNCMS Prisma model rows land in. */
  model: string;
  /**
   * Where this sits in the import order. Lower runs first, so a lookup target
   * always exists before the row pointing at it.
   */
  order: number;
  fields: readonly ZohoFieldMapping[];
}

/**
 * Zoho names every record's own id `Id` (and `Record Id` in some exports).
 * Present on every module, so it is defined once.
 */
export const ZOHO_RECORD_ID: ZohoFieldMapping = {
  zoho: ['Id', 'Record Id'],
  field: 'zohoId',
  kind: 'string',
  notes: 'Preserved verbatim; every relationship in the export is rebuilt from it.',
};

/*
 * Zoho's own column headings, spelled exactly as Zoho exports them.
 *
 * These strings are the other system's vocabulary, not ours. We call the
 * person a business manager; Zoho calls them the owner, and a CSV coming out
 * of Zoho says "Owner". Renaming them here would simply stop the import
 * recognising the column.
 */
const BUSINESS_MANAGER: ZohoFieldMapping = {
  zoho: [
    'Owner',
    'Record Owner',
    'Deal Owner',
    'Contact Owner',
    'Account Owner',
    'Lead Owner',
  ],
  field: 'businessManagerId',
  kind: 'lookup',
  lookupModule: 'Users',
  notes: 'Falls back to the importing admin when the Zoho owner has no WHNCMS user.',
};

/**
 * Zoho's timestamps. Imported rather than stamped with the import time — the
 * record's real history is more useful than the date somebody ran a migration.
 */
const TIMESTAMPS: readonly ZohoFieldMapping[] = [
  { zoho: ['Created Time'], field: 'createdAt', kind: 'datetime' },
  { zoho: ['Modified Time'], field: 'updatedAt', kind: 'datetime' },
];

const AUDIT_FIELDS: readonly ZohoFieldMapping[] = [
  ...TIMESTAMPS,
  {
    zoho: ['Created By'],
    field: 'createdById',
    kind: 'lookup',
    lookupModule: 'Users',
  },
];

/**
 * Zoho's address blocks, which repeat with a prefix per module: `Billing
 * Street` on Accounts, `Mailing Street` on Contacts, and a bare `Street` on
 * Leads. One alias each is enough — `normalizeZohoName` already makes the
 * underscore and space spellings the same key.
 */
const ADDRESS = (prefix: string, target: string): readonly ZohoFieldMapping[] => {
  const name = (part: string) => (prefix ? `${prefix} ${part}` : part);
  // `billing` + `Street` → billingStreet; no prefix → street. Leads carry a
  // single unprefixed address in both systems.
  const column = (part: string) =>
    target ? `${target}${part}` : `${part[0]!.toLowerCase()}${part.slice(1)}`;

  return [
    { zoho: [name('Street')], field: column('Street'), kind: 'string' },
    { zoho: [name('City')], field: column('City'), kind: 'string' },
    { zoho: [name('State')], field: column('State'), kind: 'string' },
    {
      // Zoho names this `Code` under a prefix and `Zip_Code` on Leads.
      zoho: prefix ? [name('Code')] : ['Zip Code'],
      field: column('PostalCode'),
      kind: 'string',
    },
    { zoho: [name('Country')], field: column('Country'), kind: 'string' },
  ];
};

// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------

const USERS: ZohoModuleMapping = {
  module: 'Users',
  label: 'Users',
  model: 'User',
  order: 10,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Email'], field: 'email', kind: 'email', required: true },
    { zoho: ['First Name'], field: 'firstName', kind: 'string' },
    { zoho: ['Last Name'], field: 'lastName', kind: 'string', required: true },
    { zoho: ['Phone'], field: 'phone', kind: 'phone' },
    {
      zoho: ['Role'],
      field: 'role',
      kind: 'enum',
      enumValues: {
        CEO: 'ADMIN',
        Administrator: 'ADMIN',
        Manager: 'MANAGER',
        'Sales Manager': 'MANAGER',
        'Sales Rep': 'SALES_REP',
        Standard: 'SALES_REP',
      },
      notes:
        'Zoho roles are a hierarchy, not a permission set; anything unmapped becomes SALES_REP.',
    },
    { zoho: ['Reporting To'], field: 'managerId', kind: 'lookup', lookupModule: 'Users' },
    { zoho: ['Title'], field: 'title', kind: 'string' },
  ],
};

const ACCOUNTS: ZohoModuleMapping = {
  module: 'Accounts',
  label: 'Accounts',
  model: 'Account',
  order: 20,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Account Name'], field: 'name', kind: 'string', required: true },
    BUSINESS_MANAGER,
    { zoho: ['Phone'], field: 'phone', kind: 'phone' },
    { zoho: ['Website'], field: 'website', kind: 'url' },
    { zoho: ['Industry'], field: 'businessCategory', kind: 'string' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    {
      zoho: ['Parent Account'],
      field: 'parentId',
      kind: 'lookup',
      lookupModule: 'Accounts',
      notes: 'Self-reference; resolved in a second pass once every account exists.',
    },
    ...ADDRESS('Billing', 'billing'),
    ...ADDRESS('Shipping', 'shipping'),
    ...AUDIT_FIELDS,
  ],
};

const CONTACTS: ZohoModuleMapping = {
  module: 'Contacts',
  label: 'Contacts',
  model: 'Contact',
  order: 30,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['First Name'], field: 'firstName', kind: 'string' },
    { zoho: ['Last Name'], field: 'lastName', kind: 'string', required: true },
    BUSINESS_MANAGER,
    { zoho: ['Email'], field: 'email', kind: 'email' },
    { zoho: ['Phone'], field: 'phone', kind: 'phone' },
    { zoho: ['Mobile'], field: 'mobile', kind: 'phone' },
    { zoho: ['Title'], field: 'title', kind: 'string' },
    { zoho: ['Department'], field: 'department', kind: 'string' },
    {
      zoho: ['Account Name'],
      field: 'accountId',
      kind: 'lookup',
      lookupModule: 'Accounts',
    },
    {
      zoho: ['Reports To'],
      field: 'reportsToId',
      kind: 'lookup',
      lookupModule: 'Contacts',
    },
    { zoho: ['Lead Source'], field: 'leadSource', kind: 'enum' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    { zoho: ['Date of Birth'], field: 'birthdate', kind: 'date' },
    ...ADDRESS('Mailing', 'mailing'),
    ...AUDIT_FIELDS,
  ],
};

const LEADS: ZohoModuleMapping = {
  module: 'Leads',
  label: 'Leads',
  model: 'Lead',
  order: 40,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['First Name'], field: 'firstName', kind: 'string' },
    { zoho: ['Last Name'], field: 'lastName', kind: 'string', required: true },
    { zoho: ['Company'], field: 'businessName', kind: 'string', required: true },
    BUSINESS_MANAGER,
    { zoho: ['Email'], field: 'email', kind: 'email' },
    { zoho: ['Phone'], field: 'phone', kind: 'phone' },
    { zoho: ['Mobile'], field: 'mobile', kind: 'phone' },
    { zoho: ['Title', 'Designation'], field: 'title', kind: 'string' },
    { zoho: ['Website'], field: 'website', kind: 'url' },
    { zoho: ['Industry'], field: 'businessCategory', kind: 'string' },
    { zoho: ['Lead Source'], field: 'source', kind: 'enum' },
    { zoho: ['Lead Status'], field: 'status', kind: 'enum' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    {
      zoho: ['Rating'],
      field: 'score',
      kind: 'number',
      derived: true,
      notes: 'WHNCMS rescores on import; Zoho ratings are free text on many editions.',
    },
    ...ADDRESS('', ''),
    ...AUDIT_FIELDS,
  ],
};

const PRODUCTS: ZohoModuleMapping = {
  module: 'Products',
  label: 'Products',
  model: 'Product',
  order: 50,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Product Name'], field: 'name', kind: 'string', required: true },
    { zoho: ['Product Code'], field: 'sku', kind: 'string' },
    BUSINESS_MANAGER,
    { zoho: ['Unit Price'], field: 'unitPrice', kind: 'money' },
    { zoho: ['Product Category'], field: 'category', kind: 'string' },
    { zoho: ['Product Active'], field: 'isActive', kind: 'boolean' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    { zoho: ['Vendor Name'], field: 'vendorId', kind: 'lookup', lookupModule: 'Vendors' },
    ...AUDIT_FIELDS,
  ],
};

const DEALS: ZohoModuleMapping = {
  module: 'Deals',
  label: 'Deals',
  model: 'Deal',
  order: 70,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Deal Name'], field: 'name', kind: 'string', required: true },
    BUSINESS_MANAGER,
    { zoho: ['Amount'], field: 'amount', kind: 'money' },
    { zoho: ['Closing Date'], field: 'expectedCloseDate', kind: 'date', required: true },
    {
      zoho: ['Stage'],
      field: 'stage',
      kind: 'enum',
      required: true,
      notes:
        'Matched to a PipelineStage by name; unmatched stages are created in the default pipeline.',
    },
    { zoho: ['Probability', 'Probability (%)'], field: 'probability', kind: 'percent' },
    {
      zoho: ['Expected Revenue'],
      field: 'expectedRevenue',
      kind: 'money',
      derived: true,
      notes: 'amount × probability, recomputed on every write rather than imported.',
    },
    {
      zoho: ['Reason For Loss', 'Lost Reason'],
      field: 'lostReason',
      kind: 'string',
      notes: 'One of a fixed list of reasons; only meaningful once the deal is Closed Lost.',
    },
    {
      zoho: ['Account Name'],
      field: 'accountId',
      kind: 'lookup',
      lookupModule: 'Accounts',
    },
    {
      zoho: ['Contact Name'],
      field: 'primaryContactId',
      kind: 'lookup',
      lookupModule: 'Contacts',
    },
    {
      zoho: ['Campaign Source'],
      field: 'campaignId',
      kind: 'lookup',
      lookupModule: 'Campaigns',
    },
    { zoho: ['Type'], field: 'type', kind: 'enum' },
    { zoho: ['Lead Source'], field: 'leadSource', kind: 'enum' },
    { zoho: ['Next Step'], field: 'nextStep', kind: 'string' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    ...AUDIT_FIELDS,
  ],
};

/** Tasks, Calls and Events all land in `activities`, split by `type`. */
const TASKS: ZohoModuleMapping = {
  module: 'Tasks',
  label: 'Tasks',
  model: 'Activity',
  order: 90,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Subject'], field: 'subject', kind: 'string', required: true },
    BUSINESS_MANAGER,
    { zoho: ['Due Date'], field: 'dueAt', kind: 'datetime' },
    { zoho: ['Status'], field: 'status', kind: 'enum' },
    { zoho: ['Priority'], field: 'priority', kind: 'enum' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    {
      zoho: ['Related To', 'What Id'],
      field: 'relatedTo',
      kind: 'lookup',
      notes:
        'Polymorphic in Zoho; resolved against every module in turn and written to the matching FK.',
    },
    {
      zoho: ['Contact Name', 'Who Id'],
      field: 'contactId',
      kind: 'lookup',
      lookupModule: 'Contacts',
    },
    ...AUDIT_FIELDS,
  ],
};

const CALLS: ZohoModuleMapping = {
  module: 'Calls',
  label: 'Calls',
  model: 'Activity',
  order: 91,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Subject'], field: 'subject', kind: 'string', required: true },
    BUSINESS_MANAGER,
    { zoho: ['Call Start Time'], field: 'startAt', kind: 'datetime' },
    { zoho: ['Call Duration'], field: 'durationMinutes', kind: 'number' },
    {
      zoho: ['Call Type'],
      field: 'direction',
      kind: 'enum',
      enumValues: { Inbound: 'INBOUND', Outbound: 'OUTBOUND', Missed: 'MISSED' },
    },
    { zoho: ['Call Result'], field: 'outcome', kind: 'string' },
    { zoho: ['Description', 'Call Agenda'], field: 'description', kind: 'text' },
    { zoho: ['Related To'], field: 'relatedTo', kind: 'lookup' },
    {
      zoho: ['Contact Name'],
      field: 'contactId',
      kind: 'lookup',
      lookupModule: 'Contacts',
    },
    ...AUDIT_FIELDS,
  ],
};

const EVENTS: ZohoModuleMapping = {
  module: 'Events',
  label: 'Meetings',
  model: 'Activity',
  order: 92,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Title', 'Subject', 'Event Title'], field: 'subject', kind: 'string', required: true },
    BUSINESS_MANAGER,
    { zoho: ['Start DateTime', 'From'], field: 'startAt', kind: 'datetime' },
    { zoho: ['End DateTime', 'To'], field: 'endAt', kind: 'datetime' },
    { zoho: ['Venue', 'Location'], field: 'location', kind: 'string' },
    { zoho: ['All day'], field: 'allDay', kind: 'boolean' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    { zoho: ['Related To'], field: 'relatedTo', kind: 'lookup' },
    ...AUDIT_FIELDS,
  ],
};

const VENDORS: ZohoModuleMapping = {
  module: 'Vendors',
  label: 'Vendors',
  model: 'Vendor',
  order: 45,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Vendor Name'], field: 'name', kind: 'string', required: true },
    BUSINESS_MANAGER,
    { zoho: ['Email'], field: 'email', kind: 'email' },
    { zoho: ['Phone'], field: 'phone', kind: 'phone' },
    { zoho: ['Website'], field: 'website', kind: 'url' },
    { zoho: ['Category'], field: 'category', kind: 'string' },
    { zoho: ['GL Account'], field: 'glAccount', kind: 'string' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    ...ADDRESS('', ''),
    ...AUDIT_FIELDS,
  ],
};

const CAMPAIGNS: ZohoModuleMapping = {
  module: 'Campaigns',
  label: 'Campaigns',
  model: 'Campaign',
  order: 60,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Campaign Name'], field: 'name', kind: 'string', required: true },
    BUSINESS_MANAGER,
    {
      zoho: ['Type'],
      field: 'type',
      kind: 'enum',
      enumValues: {
        Conference: 'CONFERENCE',
        Webinar: 'WEBINAR',
        Advertisement: 'ADVERTISEMENT',
        'Banner Ads': 'BANNER_ADS',
        'Direct Mail': 'DIRECT_MAIL',
        Email: 'EMAIL',
        Telemarketing: 'TELEMARKETING',
        Referral: 'REFERRAL',
        'Trade Show': 'TRADE_SHOW',
        'Public Relations': 'OTHER',
        Partners: 'OTHER',
      },
    },
    {
      zoho: ['Status'],
      field: 'status',
      kind: 'enum',
      enumValues: {
        Planning: 'PLANNING',
        Active: 'ACTIVE',
        Inactive: 'INACTIVE',
        Complete: 'COMPLETE',
        Completed: 'COMPLETE',
        Aborted: 'ABORTED',
      },
    },
    { zoho: ['Start Date'], field: 'startDate', kind: 'date' },
    { zoho: ['End Date'], field: 'endDate', kind: 'date' },
    { zoho: ['Expected Revenue'], field: 'expectedRevenue', kind: 'money' },
    { zoho: ['Budgeted Cost'], field: 'budgetedCost', kind: 'money' },
    { zoho: ['Actual Cost'], field: 'actualCost', kind: 'money' },
    { zoho: ['Expected Response'], field: 'expectedResponse', kind: 'number' },
    { zoho: ['Num sent'], field: 'numbersSent', kind: 'number' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    ...AUDIT_FIELDS,
  ],
};

const QUOTES: ZohoModuleMapping = {
  module: 'Quotes',
  label: 'Quotes',
  model: 'Quote',
  order: 80,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Subject'], field: 'subject', kind: 'string', required: true },
    { zoho: ['Quote Number'], field: 'quoteNumber', kind: 'string' },
    BUSINESS_MANAGER,
    {
      zoho: ['Quote Stage'],
      field: 'status',
      kind: 'enum',
      enumValues: {
        Draft: 'DRAFT',
        Negotiation: 'PRESENTED',
        Delivered: 'PRESENTED',
        'On Hold': 'PRESENTED',
        Confirmed: 'ACCEPTED',
        Closed: 'ACCEPTED',
        Rejected: 'REJECTED',
        Cancelled: 'EXPIRED',
      },
    },
    { zoho: ['Valid Till'], field: 'validUntil', kind: 'date' },
    { zoho: ['Sub Total'], field: 'subtotal', kind: 'money' },
    { zoho: ['Discount'], field: 'discountAmount', kind: 'money' },
    { zoho: ['Tax'], field: 'taxAmount', kind: 'money' },
    { zoho: ['Grand Total'], field: 'total', kind: 'money' },
    { zoho: ['Terms and Conditions'], field: 'terms', kind: 'text' },
    { zoho: ['Deal Name'], field: 'dealId', kind: 'lookup', lookupModule: 'Deals' },
    { zoho: ['Account Name'], field: 'accountId', kind: 'lookup', lookupModule: 'Accounts' },
    { zoho: ['Contact Name'], field: 'contactId', kind: 'lookup', lookupModule: 'Contacts' },
    // Quotes carry timestamps but no created-by column, so the full audit
    // block does not apply.
    ...TIMESTAMPS,
  ],
};

const SALES_ORDERS: ZohoModuleMapping = {
  module: 'Sales_Orders',
  label: 'Sales Orders',
  model: 'SalesOrder',
  order: 82,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Subject'], field: 'subject', kind: 'string', required: true },
    { zoho: ['SO Number'], field: 'orderNumber', kind: 'string' },
    BUSINESS_MANAGER,
    {
      zoho: ['Status'],
      field: 'status',
      kind: 'enum',
      enumValues: {
        Created: 'CREATED',
        Approved: 'APPROVED',
        Delivered: 'DELIVERED',
        Cancelled: 'CANCELLED',
      },
    },
    { zoho: ['Purchase Order'], field: 'purchaseOrder', kind: 'string' },
    { zoho: ['Customer No'], field: 'customerNo', kind: 'string' },
    { zoho: ['Due Date'], field: 'dueDate', kind: 'date' },
    { zoho: ['Carrier'], field: 'carrier', kind: 'string' },
    { zoho: ['Sub Total'], field: 'subtotal', kind: 'money' },
    { zoho: ['Discount'], field: 'discountAmount', kind: 'money' },
    { zoho: ['Tax'], field: 'taxAmount', kind: 'money' },
    { zoho: ['Adjustment'], field: 'adjustment', kind: 'money' },
    { zoho: ['Grand Total'], field: 'total', kind: 'money' },
    { zoho: ['Terms and Conditions'], field: 'terms', kind: 'text' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    { zoho: ['Quote Name'], field: 'quoteId', kind: 'lookup', lookupModule: 'Quotes' },
    { zoho: ['Deal Name'], field: 'dealId', kind: 'lookup', lookupModule: 'Deals' },
    { zoho: ['Account Name'], field: 'accountId', kind: 'lookup', lookupModule: 'Accounts' },
    { zoho: ['Contact Name'], field: 'contactId', kind: 'lookup', lookupModule: 'Contacts' },
    ...AUDIT_FIELDS,
  ],
};

const INVOICES: ZohoModuleMapping = {
  module: 'Invoices',
  label: 'Invoices',
  model: 'Invoice',
  order: 84,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Subject'], field: 'subject', kind: 'string', required: true },
    { zoho: ['Invoice Number'], field: 'invoiceNumber', kind: 'string' },
    BUSINESS_MANAGER,
    {
      zoho: ['Status'],
      field: 'status',
      kind: 'enum',
      enumValues: {
        Created: 'DRAFT',
        Draft: 'DRAFT',
        Sent: 'SENT',
        Paid: 'PAID',
        Cancelled: 'CANCELLED',
        Void: 'CANCELLED',
      },
    },
    { zoho: ['Invoice Date'], field: 'invoiceDate', kind: 'date' },
    { zoho: ['Due Date'], field: 'dueDate', kind: 'date' },
    { zoho: ['Purchase Order'], field: 'purchaseOrder', kind: 'string' },
    { zoho: ['Excise Duty'], field: 'exciseDuty', kind: 'money' },
    { zoho: ['Sub Total'], field: 'subtotal', kind: 'money' },
    { zoho: ['Discount'], field: 'discountAmount', kind: 'money' },
    { zoho: ['Tax'], field: 'taxAmount', kind: 'money' },
    { zoho: ['Adjustment'], field: 'adjustment', kind: 'money' },
    { zoho: ['Grand Total'], field: 'total', kind: 'money' },
    { zoho: ['Terms and Conditions'], field: 'terms', kind: 'text' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    {
      zoho: ['Sales Order'],
      field: 'salesOrderId',
      kind: 'lookup',
      lookupModule: 'Sales_Orders',
    },
    { zoho: ['Account Name'], field: 'accountId', kind: 'lookup', lookupModule: 'Accounts' },
    { zoho: ['Contact Name'], field: 'contactId', kind: 'lookup', lookupModule: 'Contacts' },
    ...AUDIT_FIELDS,
  ],
};

const CASES: ZohoModuleMapping = {
  module: 'Cases',
  label: 'Cases',
  model: 'Case',
  order: 95,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Subject'], field: 'subject', kind: 'string', required: true },
    { zoho: ['Case Number'], field: 'caseNumber', kind: 'string' },
    BUSINESS_MANAGER,
    {
      zoho: ['Status'],
      field: 'status',
      kind: 'enum',
      enumValues: {
        New: 'NEW',
        Open: 'IN_PROGRESS',
        'In Progress': 'IN_PROGRESS',
        'On Hold': 'ON_HOLD',
        Escalated: 'ESCALATED',
        Closed: 'CLOSED',
      },
    },
    {
      zoho: ['Priority'],
      field: 'priority',
      kind: 'enum',
      enumValues: { Low: 'LOW', Medium: 'MEDIUM', High: 'HIGH', Urgent: 'URGENT' },
    },
    {
      zoho: ['Case Origin'],
      field: 'origin',
      kind: 'enum',
      enumValues: {
        Email: 'EMAIL',
        Phone: 'PHONE',
        Web: 'WEB',
        Chat: 'CHAT',
        'Social Media': 'SOCIAL',
      },
    },
    { zoho: ['Case Reason'], field: 'reason', kind: 'string' },
    { zoho: ['Type'], field: 'type', kind: 'string' },
    { zoho: ['Description'], field: 'description', kind: 'text' },
    { zoho: ['Internal Comments'], field: 'internalComments', kind: 'text' },
    { zoho: ['Solution'], field: 'resolution', kind: 'text' },
    { zoho: ['Account Name'], field: 'accountId', kind: 'lookup', lookupModule: 'Accounts' },
    { zoho: ['Contact Name'], field: 'contactId', kind: 'lookup', lookupModule: 'Contacts' },
    { zoho: ['Product Name'], field: 'productId', kind: 'lookup', lookupModule: 'Products' },
    ...AUDIT_FIELDS,
  ],
};

const SOLUTIONS: ZohoModuleMapping = {
  module: 'Solutions',
  label: 'Solutions',
  model: 'Solution',
  order: 96,
  fields: [
    ZOHO_RECORD_ID,
    { zoho: ['Solution Title'], field: 'title', kind: 'string', required: true },
    { zoho: ['Solution Number'], field: 'solutionNumber', kind: 'string' },
    BUSINESS_MANAGER,
    {
      zoho: ['Status'],
      field: 'status',
      kind: 'enum',
      enumValues: {
        Draft: 'DRAFT',
        Reviewed: 'REVIEW',
        Approved: 'APPROVED',
        Duplicate: 'OBSOLETE',
        Obsolete: 'OBSOLETE',
      },
    },
    { zoho: ['Question'], field: 'question', kind: 'text' },
    { zoho: ['Answer'], field: 'answer', kind: 'text' },
    { zoho: ['Comments'], field: 'comments', kind: 'text' },
    { zoho: ['Published'], field: 'published', kind: 'boolean' },
    { zoho: ['Product Name'], field: 'productId', kind: 'lookup', lookupModule: 'Products' },
    ...AUDIT_FIELDS,
  ],
};

/**
 * The dictionary, in import order.
 *
 * `order` is what makes relationship reconstruction possible: a module's
 * lookups may only point at modules with a lower order (or at itself, which a
 * second pass resolves), so a target row always exists before the row that
 * references it. The test suite enforces that.
 *
 * Notes and Attachments are absent on purpose. They are not modules with their
 * own field sets — a Zoho export carries them as per-record attachments keyed
 * by the parent's Record Id, which the importer handles as part of whichever
 * module owns them.
 */
export const ZOHO_MODULES: readonly ZohoModuleMapping[] = [
  USERS,
  ACCOUNTS,
  CONTACTS,
  LEADS,
  VENDORS,
  PRODUCTS,
  CAMPAIGNS,
  DEALS,
  QUOTES,
  SALES_ORDERS,
  INVOICES,
  TASKS,
  CALLS,
  EVENTS,
  CASES,
  SOLUTIONS,
];

/**
 * Normalises a header for comparison: case, spaces and underscores all stop
 * mattering, so `Last_Name`, `Last Name` and `last name` are one key.
 */
export function normalizeZohoName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/gu, '_');
}

/** The module entry for a Zoho module name, or undefined if unmapped. */
export function zohoModule(module: ZohoModuleName): ZohoModuleMapping | undefined {
  return ZOHO_MODULES.find((entry) => entry.module === module);
}

/**
 * Matches one export header to a field.
 *
 * Returns undefined for anything unrecognised, which is not an error: those
 * are the columns the wizard offers as custom fields.
 */
export function matchZohoField(
  module: ZohoModuleMapping,
  header: string,
): ZohoFieldMapping | undefined {
  const wanted = normalizeZohoName(header);
  return module.fields.find((field) =>
    field.zoho.some((alias) => normalizeZohoName(alias) === wanted),
  );
}

/** Auto-mapping for a whole header row, with the leftovers named separately. */
export function autoMapHeaders(
  module: ZohoModuleMapping,
  headers: readonly string[],
): { mapped: Record<string, ZohoFieldMapping>; unmapped: string[] } {
  const mapped: Record<string, ZohoFieldMapping> = {};
  const unmapped: string[] = [];

  for (const header of headers) {
    const field = matchZohoField(module, header);
    if (field) mapped[header] = field;
    else unmapped.push(header);
  }

  return { mapped, unmapped };
}

/** Required fields the header row does not supply — the wizard blocks on these. */
export function missingRequiredFields(
  module: ZohoModuleMapping,
  headers: readonly string[],
): ZohoFieldMapping[] {
  const present = new Set(headers.map(normalizeZohoName));
  return module.fields.filter(
    (field) => field.required && !field.zoho.some((alias) => present.has(normalizeZohoName(alias))),
  );
}
